/**
 * Safety Intelligent Agent — floating chat widget.
 *
 * Mounted once in the authenticated app shell, gated by the `safety_assistant`
 * module (platform-admin toggle; see App.js). Talks to the backend
 * /api/assistant endpoints via assistantClient. Answers render as markdown.
 *
 * The agent never writes safety records. Draft tools return validated
 * prefill payloads as `actions` on the SSE done event; the widget renders an
 * "Open pre-filled form" button (with the mandatory AI warning) that stashes
 * the draft (sessionStorage, consume-on-read) and navigates to the create
 * page, where the user reviews and submits it themselves.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, FileText, AlertTriangle, HelpCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import assistantClient, { streamMessage } from '@/services/assistantClient';
import { stashDraft, CREATE_ROUTES } from '../../utils/aiPrefill/handoff';
import { AI_WARNING_TEXT } from './AIDraftWarningBanner';

export default function AssistantWidget() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Hourly AI-draft cap: {message, until} — pinned under the header until the
  // wait elapses, so "when can I try again?" is answered without re-reading
  // the conversation.
  const [limitNotice, setLimitNotice] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  // Tick only while a countdown is actually showing.
  useEffect(() => {
    if (!limitNotice?.until) return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [limitNotice?.until]);

  const limitSecondsLeft = limitNotice?.until
    ? Math.max(Math.ceil((limitNotice.until - nowTick) / 1000), 0)
    : null;

  // Self-dismiss the moment the cap frees up.
  useEffect(() => {
    if (limitNotice?.until && limitSecondsLeft === 0) setLimitNotice(null);
  }, [limitSecondsLeft, limitNotice]);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, open]);

  const ensureConversation = async () => {
    if (conversationId) return conversationId;
    const { data } = await assistantClient.post('/conversations', {});
    setConversationId(data.id);
    return data.id;
  };

  // Replace the trailing streaming assistant bubble (used on error to remove the
  // empty placeholder so only the error banner shows).
  const dropStreamingPlaceholder = () =>
    setMessages((m) => {
      const c = [...m];
      const last = c[c.length - 1];
      if (last && last.role === 'assistant' && last.streaming) c.pop();
      return c;
    });

  const errorFor = (status) => {
    if (status === 503) return 'The assistant is temporarily unavailable. Please try again later.';
    if (status === 504) return 'The assistant timed out. Please try a simpler question.';
    if (status === 403) return 'You do not have access to the Safety assistant.';
    return 'Something went wrong. Please try again.';
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setError(null);
    setInput('');
    // Add the user turn and an empty assistant bubble we stream tokens into.
    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '', streaming: true }]);
    setLoading(true);
    try {
      const id = await ensureConversation();
      await streamMessage(id, text, {
        onDelta: (chunk) =>
          setMessages((m) => {
            const c = [...m];
            const last = c[c.length - 1];
            if (last && last.role === 'assistant') {
              c[c.length - 1] = { ...last, content: (last.content || '') + chunk };
            }
            return c;
          }),
        onDone: (evt) => {
          // A draft blocked by the hourly cap is a system condition, not an
          // answer — pin it under the header rather than relying on the model
          // to relay the wait time accurately inside its prose.
          const limited = (evt.actions || []).find((a) => a?.type === 'rate_limited');
          if (limited) {
            setLimitNotice({
              message: limited.message,
              until: limited.retry_after
                ? Date.now() + limited.retry_after * 1000
                : null,
            });
          }
          setMessages((m) => {
            const c = [...m];
            const last = c[c.length - 1];
            if (last && last.role === 'assistant') {
              // The done event carries the authoritative, sanitized answer —
              // plus any draft-form actions (server-validated payloads).
              c[c.length - 1] = {
                ...last,
                content: evt.answer || last.content || '(no answer)',
                streaming: false,
                actions: (evt.actions || []).filter((a) => a?.type === 'open_prefill_form'),
              };
            }
            return c;
          });
        },
        onError: (evt) => {
          dropStreamingPlaceholder();
          setError(errorFor(evt?.status));
        },
      });
    } catch (e) {
      dropStreamingPlaceholder();
      setError(errorFor(e?.response?.status));
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Chat → form handoff: stash the validated draft, close, navigate. The
  // create page consumes the stash on mount and raises the mandatory
  // AI warning banner through the same apply path as the form's own prefill.
  const openPrefillForm = (action) => {
    const route = CREATE_ROUTES[action.module];
    if (!route) return;
    stashDraft(action.module, {
      draft: action.draft,
      generation_id: action.generation_id,
      field_sources: action.field_sources,
      certainty: action.certainty,
      warnings: action.warnings,
      rationale: action.rationale,
    });
    setOpen(false);
    navigate(route);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open Ask Niq"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition hover:bg-blue-700"
      >
        <MessageCircle className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 flex h-[32rem] w-96 max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between bg-blue-600 px-4 py-3 text-white">
        <div>
          <div className="text-sm font-semibold">Ask Niq</div>
          <div className="text-[11px] opacity-80">Q&amp;A + form drafting · never saves without your review</div>
        </div>
        <div className="flex items-center gap-1">
          {/* Same deep-link shape as HelpButton: the protected /help route
              loads the portal and applies the tenant's module filter. */}
          <a
            href="/help#ask-niq"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open Ask Niq help"
            title="Help"
            className="rounded p-1 hover:bg-blue-700"
          >
            <HelpCircle className="h-5 w-5" />
          </a>
          <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="rounded p-1 hover:bg-blue-700">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Hourly AI-draft cap. Sits directly under the header so it stays
          visible as the conversation scrolls, and counts down to the exact
          moment a slot frees up rather than repeating a stale "in about N
          minutes". Q&A still works while this is showing — only drafting is
          capped — which is why the wording is specific to drafts. */}
      {limitNotice && (
        <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <div className="flex-1">
            <div>{limitNotice.message}</div>
            {limitSecondsLeft > 0 && (
              <div className="mt-0.5 font-medium">
                Try again in{' '}
                {limitSecondsLeft >= 60
                  ? `${Math.floor(limitSecondsLeft / 60)}m ${String(limitSecondsLeft % 60).padStart(2, '0')}s`
                  : `${limitSecondsLeft}s`}
                . You can still ask questions meanwhile.
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setLimitNotice(null)}
            aria-label="Dismiss draft limit notice"
            className="rounded p-0.5 hover:bg-amber-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 space-y-3 overflow-y-auto bg-gray-50 px-3 py-3">
        {messages.length === 0 && (
          <div className="mt-6 px-2 text-center text-sm text-gray-500">
            Ask about your safety data — e.g. <em>“how many open incidents?”</em> or <em>“list overdue CAPAs”</em>.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
            <div
              className={
                m.role === 'user'
                  ? 'max-w-[85%] rounded-lg bg-blue-600 px-3 py-2 text-sm text-white'
                  : 'max-w-[85%] rounded-lg bg-white px-3 py-2 text-sm text-gray-800 shadow-sm ring-1 ring-gray-200'
              }
            >
              {m.role === 'assistant' ? (
                m.streaming && !m.content ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
                  </div>
                ) : (
                  <>
                    <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1">
                      <ReactMarkdown>{m.content}</ReactMarkdown>
                    </div>
                    {(m.actions || []).map((a, ai) => (
                      <div key={ai} className="mt-2 border-t border-gray-100 pt-2">
                        <button
                          type="button"
                          onClick={() => openPrefillForm(a)}
                          className="flex w-full items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                        >
                          <FileText className="h-4 w-4" />
                          {a.label || `Open a pre-filled ${(a.module || '').toUpperCase()} form`}
                        </button>
                        <p className="mt-1.5 flex items-start gap-1 text-[11px] leading-snug text-amber-700">
                          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                          {AI_WARNING_TEXT}
                        </p>
                      </div>
                    ))}
                  </>
                )
              ) : (
                m.content
              )}
            </div>
          </div>
        ))}
        {error && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">{error}</div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 bg-white p-2">
        <div className="flex items-end gap-2">
          <textarea
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask about your safety data…"
            className="max-h-28 flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            type="button"
            onClick={send}
            disabled={loading || !input.trim()}
            aria-label="Send"
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
